<template>
    <div v-if="switchOptions?.switch_url" :class="isAiProduct ? 'mt-3 mb-4' : 'my-4'">
        <div class="flex justify-center">
            <template v-if="hasDynamicLabels">
                <div class="flex w-full items-center gap-1 rounded-md border border-gray-200 p-0.5 whitespace-nowrap shadow-xs">
                    <template v-for="label in ['label1', 'label2']" :key="label">
                        <Link
                            v-if="switchOptions.selected_label !== label"
                            :href="switchOptions.switch_url"
                            preserve-scroll
                            class="w-full rounded-md bg-transparent p-2 text-center transition-all duration-200 ease-in-out hover:bg-primary hover:font-bold hover:text-white"
                            @click="onSwitchClick"
                        >
                            {{ getSwitchOptionValue(label) }}
                        </Link>

                        <span v-else class="w-full rounded-md bg-primary p-2 text-center font-bold text-white">
                            {{ getSwitchOptionValue(label) }}
                        </span>
                    </template>
                </div>
            </template>
            <template v-else>
                <div class="flex flex-row items-center gap-3 whitespace-nowrap">
                    <template v-if="isCollectionProduct">
                        <Link
                            :href="switchOptions.switch_url"
                            preserve-scroll
                            class="flex-1 rounded-full bg-[#117a65] px-6 py-2 font-bold text-white"
                            @click="onSwitchClick"
                        >
                            <span>{{ I18n.global.t('pages.product_detail_page.product_info.switch_options.with_name') }}</span>
                        </Link>
                        <span class="flex-1 rounded-full bg-primary px-6 py-2 font-bold text-white">
                            <span>{{ I18n.global.t('pages.product_detail_page.product_info.switch_options.without_name') }}</span>
                        </span>
                    </template>

                    <template v-else>
                        <span class="flex-1 rounded-full bg-primary px-6 py-2 font-bold text-white">
                            <span>{{ I18n.global.t('pages.product_detail_page.product_info.switch_options.with_name') }}</span>
                        </span>
                        <Link
                            :href="switchOptions.switch_url"
                            preserve-scroll
                            class="flex-1 rounded-full bg-[#117a65] px-6 py-2 font-bold text-white"
                            @click="onSwitchClick"
                        >
                            <span>{{ I18n.global.t('pages.product_detail_page.product_info.switch_options.without_name') }}</span>
                        </Link>
                    </template>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import I18n from '@/i18n';
import { useAiPhotoTransfer } from '@/composables/ai-upload/useAiPhotoTransfer';
import { useAiImageGenerationSessionStore } from '@/stores/ai-upload/AiImageGenerationSessionStore';
import { useProductPageStore } from '@/stores/pages/ProductPageStore';
import { ConfigurableProductTypeEnum } from '@/types/generated';
import { Link } from '@inertiajs/vue3';
import { computed } from 'vue';

const productPageStore = useProductPageStore();
const switchOptions = productPageStore.configurableProduct.data?.switch_option;
const isCollectionProduct = productPageStore.configurableProduct.type === ConfigurableProductTypeEnum.COLLECTION;

// On the AI page the related-products slider above this is usually empty, so the switch sits
// straight under the title and needs a smaller top margin than the other products.
const isAiProduct = productPageStore.configurableProduct.type === ConfigurableProductTypeEnum.AI;

const hasDynamicLabels = computed(() => !!switchOptions?.label1 && !!switchOptions?.label2);

/**
 * Crossing between the halves of an AI pair carries the customer's photo over, so tell the sibling to
 * generate with it on arrival rather than making them tap again. Only the AI pair — for every other
 * product this switch has nothing to carry.
 *
 * Only once this half has actually revealed a result. The carried photo is stored the moment it is
 * cropped, so without this the intent was also claimed by someone switching mid-generation: they left
 * a generation they had not seen yet and the sibling immediately spent a second daily credit on the
 * same photo. Falling through leaves the primed button, which they can still tap themselves.
 */
function onSwitchClick(): void {
    if (!isAiProduct) {
        return;
    }

    if (!useAiImageGenerationSessionStore().isRevealGateOpen) {
        return;
    }

    useAiPhotoTransfer().markVariantSwitch();
}

function getSwitchOptionValue(label: string): string {
    if (!switchOptions) {
        return '';
    }
    return switchOptions[label as keyof typeof switchOptions] ?? '';
}
</script>
